/**
 * UpdateBanner — global, non-blocking pill shown from any route when a new
 * version is detected. Clicking applies the update (delegated to
 * versionWatcher.triggerReload, which respects active exam/practice).
 *
 * Hidden during the full-screen reload overlay, when no update is pending,
 * and on auth routes (so it never blocks login).
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { versionWatcher, type WatcherState } from "@/lib/version-watcher";

const HIDDEN_PATH_PREFIXES = ["/auth", "/unsubscribe"];

export const UpdateBanner = () => {
  const [state, setState] = useState<WatcherState>(() => versionWatcher.getState());
  const [dismissed, setDismissed] = useState(false);
  const [clicking, setClicking] = useState(false);
  const location = useLocation();

  useEffect(() => versionWatcher.subscribe(setState), []);

  // Re-show on each new server version
  useEffect(() => {
    setDismissed(false);
  }, [state.serverVersion]);

  if (state.disabled) return null;
  if (!state.shouldReload) return null;
  if (!state.bannerVisible) return null;
  if (state.reloadsThisSession > 0) return null; // overlay is taking over
  if (dismissed) return null;
  if (location.pathname === "/") return null;
  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const handleClick = async () => {
    if (clicking) return;
    setClicking(true);
    try {
      await versionWatcher.triggerReload("user-banner");
    } finally {
      setClicking(false);
    }
  };

  const deferred = state.examActive;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 top-3 z-[2147483646] flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-card/95 px-3 py-1.5 text-xs shadow backdrop-blur sm:text-sm"
    >
      <span className="text-foreground">
        {deferred
          ? "Nueva versión disponible. Se aplicará al terminar."
          : "Nueva versión disponible."}
      </span>
      {!deferred && (
        <button
          type="button"
          onClick={handleClick}
          disabled={clicking}
          className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
        >
          {clicking ? "Actualizando…" : "Actualizar ahora"}
        </button>
      )}
      <button
        type="button"
        aria-label="Cerrar aviso de actualización"
        onClick={() => setDismissed(true)}
        className="ml-1 rounded-full px-1.5 text-muted-foreground hover:text-foreground"
      >
        ×
      </button>
    </div>
  );
};

export default UpdateBanner;
